import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save, ShieldCheck, AlertTriangle, Loader2, Eye, EyeOff, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

export default function StripeSettings() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({
    mode: "sandbox",
    test_secret_key: "",
    test_publishable_key: "",
    test_webhook_secret: "",
    live_secret_key: "",
    live_publishable_key: "",
    live_webhook_secret: "",
  });
  const [show, setShow] = useState({ test_secret: false, live_secret: false, test_webhook: false, live_webhook: false });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/stripe-settings");
      setData(data);
      setForm((f) => ({ ...f, mode: data.mode || "sandbox", test_publishable_key: data.test_publishable_key || "", live_publishable_key: data.live_publishable_key || "" }));
    } catch {
      navigate("/admin");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/stripe-settings", form);
      // Reset secret-key inputs (they're write-only — masked on read)
      setForm((f) => ({
        ...f,
        test_secret_key: "", test_webhook_secret: "",
        live_secret_key: "", live_webhook_secret: "",
      }));
      await load();
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/stripe-settings/test");
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Verbindung fehlgeschlagen" });
    } finally { setTesting(false); }
  };

  if (loading || !data) return <div className="p-12 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Lade…</div>;

  const isLive = form.mode === "live";

  return (
    <AdminLayout>
      <main className="max-w-5xl mx-auto px-6 py-12">
        <p className="mv-label mb-2">Zahlungsabwicklung</p>
        <h1 className="font-display text-4xl mb-2">Stripe API-Keys</h1>
        <p className="text-sm mb-8 max-w-2xl" style={{ color: "var(--mv-text-muted)" }}>
          Hinterlege Sandbox- und Live-Keys für Stripe. Der gewählte Modus wird sofort wirksam — alle Checkouts, Webhooks und Subscriptions
          verwenden ab Speichern den aktiven Schlüssel. Geheime Schlüssel werden verschlüsselt gespeichert und nur maskiert angezeigt.
        </p>

        {data.env_fallback && (
          <div className="mv-card p-4 mb-6 flex items-start gap-3" style={{ background: "#FEF3C7", borderColor: "#FCD34D" }} data-testid="stripe-env-fallback-banner">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" strokeWidth={1.5} style={{ color: "#92400E" }} />
            <div>
              <p className="text-sm font-medium" style={{ color: "#92400E" }}>Noch nicht konfiguriert</p>
              <p className="text-xs mt-1" style={{ color: "#92400E" }}>Aktuell werden die Schlüssel aus der `STRIPE_API_KEY`-Umgebungsvariable verwendet. Hinterlege jetzt deine echten Keys, um Sandbox/Live umschaltbar zu machen.</p>
            </div>
          </div>
        )}

        <form onSubmit={save} className="space-y-6">
          {/* Mode toggle */}
          <div className="mv-card p-6" data-testid="stripe-mode-card">
            <p className="mv-label mb-3">Aktiver Modus</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { val: "sandbox", label: "Sandbox / Test", hint: "Keine echten Zahlungen. Test-Karten möglich.", color: "#10B981" },
                { val: "live", label: "Live / Produktion", hint: "Echte Zahlungen. Echte Karten.", color: "#DC2626" },
              ].map((opt) => (
                <button
                  key={opt.val}
                  type="button"
                  onClick={() => setForm({ ...form, mode: opt.val })}
                  data-testid={`stripe-mode-${opt.val}`}
                  className="p-4 text-left border-2 rounded-lg transition-all"
                  style={{
                    borderColor: form.mode === opt.val ? opt.color : "var(--mv-border)",
                    background: form.mode === opt.val ? `${opt.color}10` : "transparent",
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: opt.color }} />
                    <p className="font-medium text-sm">{opt.label}</p>
                  </div>
                  <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>{opt.hint}</p>
                </button>
              ))}
            </div>
            {isLive && (
              <div className="mt-4 p-3 rounded-md text-xs flex items-start gap-2" style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid="stripe-live-warning">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" strokeWidth={1.6} />
                <span><strong>Live-Modus aktiv:</strong> Echte Karten werden belastet und Auszahlungen fließen auf das hinterlegte Stripe-Konto. Stelle sicher, dass die Live-Keys korrekt eingegeben sind.</span>
              </div>
            )}
          </div>

          {/* Sandbox keys */}
          <div className="mv-card p-6" data-testid="stripe-test-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="mv-label">Sandbox / Test Keys</p>
                <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>
                  Beginnen mit <code className="text-[10px] px-1 py-0.5 rounded bg-[var(--mv-surface)]">sk_test_</code> bzw. <code className="text-[10px] px-1 py-0.5 rounded bg-[var(--mv-surface)]">pk_test_</code>. Aus dem Stripe-Dashboard im Test-Modus.
                </p>
              </div>
              {data.has_test_secret && <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded" style={{ background: "#D1FAE5", color: "#065F46" }}>Gesetzt</span>}
            </div>
            <SecretField
              label="Secret Key"
              testId="stripe-test-secret"
              placeholder={data.test_secret_key_masked || "sk_test_..."}
              value={form.test_secret_key}
              onChange={(v) => setForm({ ...form, test_secret_key: v })}
              show={show.test_secret}
              onToggle={() => setShow((s) => ({ ...s, test_secret: !s.test_secret }))}
              hasExisting={data.has_test_secret}
            />
            <div className="mt-3">
              <label className="block text-xs font-medium mb-1.5">Publishable Key</label>
              <input className="mv-input font-mono text-sm" data-testid="stripe-test-publishable" value={form.test_publishable_key} onChange={(e) => setForm({ ...form, test_publishable_key: e.target.value })} placeholder="pk_test_..." />
            </div>
            <SecretField
              label="Webhook Signing Secret"
              testId="stripe-test-webhook"
              placeholder={data.test_webhook_secret_masked || "whsec_..."}
              value={form.test_webhook_secret}
              onChange={(v) => setForm({ ...form, test_webhook_secret: v })}
              show={show.test_webhook}
              onToggle={() => setShow((s) => ({ ...s, test_webhook: !s.test_webhook }))}
              hasExisting={!!data.test_webhook_secret_masked}
              className="mt-3"
            />
          </div>

          {/* Live keys */}
          <div className="mv-card p-6" data-testid="stripe-live-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="mv-label">Live / Produktion Keys</p>
                <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>
                  Beginnen mit <code className="text-[10px] px-1 py-0.5 rounded bg-[var(--mv-surface)]">sk_live_</code> bzw. <code className="text-[10px] px-1 py-0.5 rounded bg-[var(--mv-surface)]">pk_live_</code>.
                </p>
              </div>
              {data.has_live_secret && <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded" style={{ background: "#D1FAE5", color: "#065F46" }}>Gesetzt</span>}
            </div>
            <SecretField
              label="Secret Key"
              testId="stripe-live-secret"
              placeholder={data.live_secret_key_masked || "sk_live_..."}
              value={form.live_secret_key}
              onChange={(v) => setForm({ ...form, live_secret_key: v })}
              show={show.live_secret}
              onToggle={() => setShow((s) => ({ ...s, live_secret: !s.live_secret }))}
              hasExisting={data.has_live_secret}
            />
            <div className="mt-3">
              <label className="block text-xs font-medium mb-1.5">Publishable Key</label>
              <input className="mv-input font-mono text-sm" data-testid="stripe-live-publishable" value={form.live_publishable_key} onChange={(e) => setForm({ ...form, live_publishable_key: e.target.value })} placeholder="pk_live_..." />
            </div>
            <SecretField
              label="Webhook Signing Secret"
              testId="stripe-live-webhook"
              placeholder={data.live_webhook_secret_masked || "whsec_..."}
              value={form.live_webhook_secret}
              onChange={(v) => setForm({ ...form, live_webhook_secret: v })}
              show={show.live_webhook}
              onToggle={() => setShow((s) => ({ ...s, live_webhook: !s.live_webhook }))}
              hasExisting={!!data.live_webhook_secret_masked}
              className="mt-3"
            />
          </div>

          {/* Test connection */}
          <div className="mv-card p-6" data-testid="stripe-test-connection-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="mv-label">Verbindung testen</p>
                <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>Ruft `Account.retrieve()` mit dem aktiven Schlüssel auf.</p>
              </div>
              <button type="button" onClick={runTest} disabled={testing} className="mv-btn-ghost text-sm" data-testid="stripe-test-connection-btn">
                {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} Test
              </button>
            </div>
            {testResult && (
              <div className="p-3 rounded-md text-xs" style={{ background: testResult.ok ? "#D1FAE5" : "#FEE2E2", color: testResult.ok ? "#065F46" : "#991B1B" }} data-testid="stripe-test-result">
                {testResult.ok ? (
                  <div>
                    <p className="font-medium mb-1">✓ Verbindung erfolgreich</p>
                    <p className="opacity-90">Account-ID: <code className="font-mono">{testResult.account_id}</code></p>
                    {testResult.email && <p className="opacity-90">E-Mail: {testResult.email}</p>}
                    {testResult.country && <p className="opacity-90">Land: {testResult.country}</p>}
                    <p className="opacity-90">Charges aktiviert: {testResult.charges_enabled ? "Ja" : "Nein"}</p>
                  </div>
                ) : (
                  <p>✗ {testResult.error}</p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 pt-4 border-t" style={{ borderColor: "var(--mv-border)" }}>
            <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-xs flex items-center gap-1.5 opacity-75 hover:opacity-100">
              <ExternalLink className="w-3 h-3" /> Stripe-Dashboard öffnen
            </a>
            <button data-testid="stripe-save-btn" type="submit" disabled={saving} className="mv-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? "Speichere…" : "Speichern & übernehmen"}
            </button>
          </div>
        </form>
      </main>
    </AdminLayout>
  );
}

function SecretField({ label, testId, value, onChange, placeholder, show, onToggle, hasExisting, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          autoComplete="off"
          className="mv-input font-mono text-sm pr-10"
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
        <button type="button" onClick={onToggle} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-[var(--mv-surface)]">
          {show ? <EyeOff className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />}
        </button>
      </div>
      {hasExisting && !value && <p className="text-[11px] opacity-60 mt-1">Schlüssel bereits gespeichert. Leer lassen, um ihn zu behalten.</p>}
    </div>
  );
}
